import React, { useEffect, useState } from 'react'
import { Typography, Card, CardContent, Divider, Box } from '@mui/material'
import { getColDefs, ColumnConfig } from 'hooks/useColumnDefs/useColumnDefs'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import { CaseData } from 'hooks/cb-api'
import { differenceInDays, format } from 'date-fns'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons'

interface CaseInfoProps {
    caseInfo: CaseData
    showClientTypeIssuerUI: boolean
}

export const CaseInfo: React.FC<CaseInfoProps> = ({
    caseInfo,
    showClientTypeIssuerUI,
}) => {
    const { user } = useAuthedUser()
    const [casedetails, setCaseDetails] = useState<ColumnConfig[]>()

    useEffect(() => {
        //@ts-ignore
        getColDefs('caseManagement', user?.role?.alias).then((res) => {
            const excludedCells = [
                'actions',
                'document',
                'assign',
                'flag',
                'case_menu',
            ]
            const excludedAccessors = ['date_completed', 'verdict']

            if (!showClientTypeIssuerUI) {
                excludedAccessors.push('merchant_alias')
            }
            if (showClientTypeIssuerUI) {
                excludedAccessors.push('last_user_action')
                excludedAccessors.push('last_action_date')
                excludedAccessors.push('verdict_date')
                excludedAccessors.push('last_action')
            }

            setCaseDetails(
                res.columns.filter((col) => {
                    const isIssuer = col.client_type === 'issuer'
                    const isMerchant = col.client_type === 'merchant'
                    const isExcludedCell = excludedCells.includes(
                        col?.cell ?? ''
                    )
                    const isExcludedAccessor = excludedAccessors.includes(
                        col?.accessor ?? ''
                    )

                    return (
                        (showClientTypeIssuerUI ? isIssuer : isMerchant) &&
                        !isExcludedCell &&
                        !isExcludedAccessor
                    )
                })
            )
        })
    }, [user, showClientTypeIssuerUI])

    const dueDate = showClientTypeIssuerUI
        ? caseInfo.due_date
        : caseInfo.date_due

    const dueDateDaysLeft = showClientTypeIssuerUI
        ? differenceInDays(
              new Date(caseInfo.due_date + 'T00:00'),
              new Date(format(new Date(), 'yyyy-MM-dd') + 'T00:00')
          )
        : differenceInDays(
              new Date(caseInfo.date_due + 'T00:00'),
              new Date(format(new Date(), 'yyyy-MM-dd') + 'T00:00')
          )

    const daysLeftColor = dueDateDaysLeft < 999 ? '#E50000' : undefined

    return (
        <Card
            id={'caseQCReviewCaseInfo'}
            style={{
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
            }}
        >
            <CardContent>
                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing(1),
                    })}
                >
                    <Box
                        className="emp-cardTitle"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingTop: '0px !important',
                        }}
                    >
                        {caseInfo.merchant_name}
                        <Box
                            sx={(theme) => ({
                                paddingLeft: theme.spacing(1),
                                paddingRight: theme.spacing(1),
                            })}
                        >
                            |
                        </Box>
                        Case ID: {caseInfo.id}
                    </Box>

                    <Box
                        className="emp-cardSubtitle"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <span>
                            Due Date:{' '}
                            <span style={{ fontWeight: 'normal' }}>
                                {dueDate}
                            </span>
                        </span>
                        <Box
                            sx={(theme) => ({
                                color: daysLeftColor,
                                paddingLeft: theme.spacing(2),
                                fontWeight: daysLeftColor
                                    ? undefined
                                    : 'normal',
                            })}
                        >
                            {daysLeftColor && (
                                <>
                                    <FontAwesomeIcon
                                        icon={faExclamationCircle}
                                        color={daysLeftColor}
                                    />{' '}
                                </>
                            )}
                            {dueDateDaysLeft} days left
                        </Box>
                    </Box>
                </Box>

                <Typography
                    variant="h6"
                    color="textSecondary"
                    className={'emp-cardTitle'}
                >
                    Case Details
                </Typography>

                <Divider className={'emp-divider'} />

                <Box
                    sx={(theme) => ({
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(150px, 1fr))',
                        gap: theme.spacing(1),
                    })}
                >
                    {casedetails?.map((column: ColumnConfig, idx: number) => {
                        //@ts-ignore
                        let fieldValue = caseInfo[column.accessor]
                            ? //@ts-ignore
                              caseInfo[column.accessor]
                            : showClientTypeIssuerUI &&
                              column.accessor === 'assigned_user'
                            ? 'Unassigned'
                            : showClientTypeIssuerUI &&
                              column.accessor === 'issuer_doc'
                            ? 'No doc'
                            : 'No info'

                        const updatedLabel =
                            showClientTypeIssuerUI &&
                            column.accessor === 'merchant_name'
                                ? 'Client'
                                : column.label
                        if (
                            column.accessor === 'assigned_user' &&
                            caseInfo[column.accessor]
                        ) {
                            fieldValue = `${
                                caseInfo[column.accessor]?.fname ?? ''
                            } ${caseInfo[column.accessor]?.lname ?? ''}`
                        }

                        return (
                            <div
                                className={'emp-caseDetailsSection'}
                                key={`key-${idx}`}
                            >
                                <Typography className={'emp-caseDetailsTitle'}>
                                    {`${updatedLabel}: `}
                                </Typography>
                                <Typography className={'emp-caseDetails'}>
                                    {fieldValue}
                                </Typography>
                            </div>
                        )
                    })}
                </Box>

                {caseInfo?.flag?.comment && (
                    <div className={'emp-caseDetailsSection'}>
                        <Typography className={'emp-caseDetailsTitle'}>
                            {`Comment: `}
                        </Typography>
                        <Typography className={'emp-caseDetails'}>
                            {caseInfo?.flag?.comment ?? 'No info'}
                        </Typography>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}
